'use client'

import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import Image from 'next/image'

import { DeliveryAreaGateModal } from 'modules/delivery'
import { AnyProductTypes, countJumpCalculation } from 'modules/products'
import { SeoListName } from 'shared/analytics'
import { ButtonVariants } from 'shared/ui/Button/Button.enums'
import { Tooltip } from 'shared/ui/Tooltip'

import useCartStore, { useDisplayCart } from '../../../../store/cartStore'
import useAddToCart from '../../hooks/useAddToCart'
import RemoveLastProductModal from '../RemoveLastProductModal'

import { IconChangeQuantity } from './IconChangeQuantity'
import ProductQuantityInput from './ProductQuantityInput'

import styles from './AddToCartButton.module.scss'

type AddToCartButtonProps = {
  size?: 'default' | 'large' | 'small'
  disabled?: boolean | number
  quantityJump?: number
  product: AnyProductTypes
  replacement?: boolean
  productId?: string
  seoButtonPlace: string
  idxOnList: number
  listName: SeoListName
  cartItemId?: string
  disabledQuantity?: boolean
  variant?: ButtonVariants
  icon?: string
  handleSyneriseRecommendationClickEvent?: () => void
  isCollapsible?: boolean
  algoliaInsightsAttributes?: Record<string, string | number | boolean>
}

const AddToCartButton = (props: AddToCartButtonProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const incrementButtonRef = useRef<HTMLButtonElement>(null)
  const [productQuantity, setProductQuantity] = useState(0)
  const [isWaitingForApi, setIsWaitingForApi] = useState(false)
  const [lastApiQuantity, setLastApiQuantity] = useState(0)
  const [localQuantity, setLocalQuantity] = useState(0)

  const {
    quantityJump,
    disabled,
    product,
    size,
    seoButtonPlace,
    idxOnList,
    listName,
    cartItemId,
    disabledQuantity,
    variant,
    icon,
    handleSyneriseRecommendationClickEvent,
    isCollapsible = false,
    algoliaInsightsAttributes
  } = props
  const {
    addNewItem,
    handleIncrement,
    handleDecrement,
    confirmationModalOpen,
    handleRemoveModalClose: onRemoveModalClose,
    postalCodeModalOpen,
    handleRemoveFromCartSeo,
    quantityLimit,
    isAvailable,
    currentAction,
    handleClosePostalCodeModal,
    handleSetCustomQuantity,
    debouncedCartUpdate,
    handleDirectItemUpdate,
    quantityLimitMet,
    isSpecialOfferIncrementDisabled
  } = useAddToCart({
    quantityJump,
    productQuantity: localQuantity,
    cartItemId,
    product,
    seoButtonPlace,
    setProductQuantity: setLocalQuantity,
    idxOnList,
    listName
  })

  const { getProductCount, productsInProcess } = useCartStore()
  const displayCart = useDisplayCart()

  const isProductInProcess = productsInProcess.includes(product.id)

  useEffect(() => {
    if (!isProductInProcess && isWaitingForApi) {
      setIsWaitingForApi(false)
    }
  }, [isProductInProcess, isWaitingForApi])

  useEffect(() => {
    if (cartItemId) {
      const cartItem = displayCart?.items?.find(item => item.id === cartItemId)

      if (cartItem) {
        setProductQuantity(cartItem.quantity)
        setLocalQuantity(cartItem.quantity)
      }
    } else {
      const totalProductQuantity = getProductCount(product.id)

      if (!isWaitingForApi || totalProductQuantity !== lastApiQuantity) {
        setProductQuantity(totalProductQuantity)
        setLocalQuantity(totalProductQuantity)
        setLastApiQuantity(totalProductQuantity)
      }
    }
  }, [
    displayCart,
    cartItemId,
    getProductCount,
    product.id,
    isWaitingForApi,
    lastApiQuantity
  ])

  const variantClass =
    styles[`v_${variant || ButtonVariants.primary}`] ?? styles.v_primary
  const sizeClass = {
    [styles.sizeSmall]: size === 'small',
    [styles.sizeLarge]: size === 'large'
  }

  const backFocusToElement = () => {
    // button/ input is optional rendered. After change we don't want to lost focun on
    setTimeout(() => {
      if (
        incrementButtonRef.current &&
        document.contains(incrementButtonRef.current)
      ) {
        incrementButtonRef.current?.focus()
      } else if (buttonRef.current && document.contains(buttonRef.current)) {
        buttonRef.current?.focus()
      }
    }, 10)
  }

  const handleIncrementWithState = () => {
    const newQuantity = countJumpCalculation(
      'increment',
      productQuantity,
      quantityJump || 1,
      quantityLimit
    )

    setProductQuantity(newQuantity)
    setLocalQuantity(newQuantity)
    setIsWaitingForApi(true)
    setLastApiQuantity(newQuantity)

    handleIncrement()
  }

  const handleDecrementWithState = () => {
    const newQuantity = countJumpCalculation(
      'decrement',
      productQuantity,
      quantityJump || 1,
      0
    )

    if (newQuantity === 0) {
      buttonRef.current?.focus()
    }

    setProductQuantity(newQuantity)
    setLocalQuantity(newQuantity)
    setIsWaitingForApi(true)
    setLastApiQuantity(newQuantity)

    handleDecrement()
  }

  const handleAddWithState = () => {
    const initialQuantity = quantityJump || 1

    setProductQuantity(initialQuantity)

    setLocalQuantity(initialQuantity)
    setIsWaitingForApi(true)
    setLastApiQuantity(initialQuantity)

    addNewItem(initialQuantity, backFocusToElement)
    handleSyneriseRecommendationClickEvent?.()
  }

  const handleImmediateQuantityChange = (newQuantity: number) => {
    if (cartItemId) {
      const cartItem = displayCart?.items?.find(item => item.id === cartItemId)
      const currentQuantity = cartItem?.quantity || 0

      setProductQuantity(newQuantity)
      setLocalQuantity(newQuantity)
      setIsWaitingForApi(true)
      setLastApiQuantity(newQuantity)

      if (newQuantity > currentQuantity) {
        void handleSetCustomQuantity(newQuantity)
      } else if (newQuantity < currentQuantity) {
        if (newQuantity <= 0) {
          return onRemoveModalClose()
        }
        void handleSetCustomQuantity(newQuantity)
      }
    } else {
      const currentTotalQuantity = getProductCount(product.id)

      setProductQuantity(newQuantity)
      setLocalQuantity(newQuantity)
      setIsWaitingForApi(true)
      setLastApiQuantity(newQuantity)

      if (
        debouncedCartUpdate &&
        typeof debouncedCartUpdate.cancel === 'function'
      ) {
        debouncedCartUpdate.cancel()
      }

      const cartItems =
        displayCart?.items?.filter(i => i.product?.id === product.id) || []

      // Special case 1: If we're waiting for the API and the cart doesn't yet show the product
      if (isWaitingForApi && cartItems.length === 0) {
        addNewItem(newQuantity)

        return
      }

      // Special case 2: If we have a temporary cart item (just added) and we're changing the quantity
      const isTemporaryItem =
        cartItems.length === 1 && cartItems[0].id.startsWith('temp-')

      if (
        isTemporaryItem &&
        currentTotalQuantity === 1 &&
        newQuantity > currentTotalQuantity
      ) {
        addNewItem(newQuantity)

        return
      }

      if (cartItems.length > 0) {
        if (newQuantity > currentTotalQuantity) {
          if (cartItems.length === 1) {
            const item = cartItems[0]

            handleDirectItemUpdate(item.id, newQuantity)
          } else {
            const firstItem = cartItems[0]

            const quantityDelta = newQuantity - currentTotalQuantity

            const newItemQuantity = firstItem.quantity + quantityDelta

            handleDirectItemUpdate(firstItem.id, newItemQuantity)
          }
        } else if (newQuantity < currentTotalQuantity) {
          if (newQuantity <= 0) {
            cartItems.forEach(item => {
              handleDirectItemUpdate(item.id, 0)
            })
          } else if (cartItems.length === 1) {
            const item = cartItems[0]

            handleDirectItemUpdate(item.id, newQuantity)
          } else {
            const firstItem = cartItems[0]

            const otherItemsQuantity = cartItems
              .slice(1)
              .reduce((sum, item) => sum + item.quantity, 0)

            const newFirstItemQuantity = Math.max(
              0,
              newQuantity - otherItemsQuantity
            )

            handleDirectItemUpdate(firstItem.id, newFirstItemQuantity)
          }
        }
      } else {
        addNewItem(newQuantity)
      }
    }
  }

  const handleRemoveModalClose = () => {
    onRemoveModalClose()
    setTimeout(() => {
      backFocusToElement()
    }, 10)
  }
  const displayButtonContent = () => {
    if (disabled || !isAvailable) {
      return (
        <button
          className={clsx(
            'add_to_cart_disabled_button',
            styles.button,
            variantClass,
            sizeClass,
            styles.disabled
          )}
          disabled
          aria-label='Produkt niedostępny'
          ref={buttonRef}
        >
          Produkt niedostępny
        </button>
      )
    }

    const displayQuantity = productQuantity

    return displayQuantity ? (
      <div
        id={product.code}
        className={clsx(
          'item_count_button',
          styles.button,
          variantClass,
          sizeClass,
          styles.quantity,
          { [styles.disabled]: disabledQuantity }
        )}
        {...{ disabled: disabledQuantity }}
      >
        <IconChangeQuantity
          className='add_to_cart_decrement_button'
          role='button'
          onClick={handleDecrementWithState}
          isLoading={false}
          disabled={!!disabledQuantity}
          type='minus'
        />

        <ProductQuantityInput
          value={displayQuantity}
          onChange={handleImmediateQuantityChange}
          quantityLimit={quantityLimit}
          disabled={disabledQuantity}
          aria-label={`Ilość produktu ${product.nameDescriptionShort || product.name || product.nameShort}`}
        />
        {displayQuantity >= quantityLimit || quantityLimitMet ? (
          <Tooltip
            placement='right'
            id={`add-to-cart-tooltip-${product.code}`}
            title='Brak większej ilości produktów w magazynie lub osiągnięto maksymalną liczbę produktów na jedno zamówienie'
          >
            <div>
              <IconChangeQuantity
                className='add_to_cart_increment_button_disabled'
                onClick={() => handleIncrementWithState()}
                isLoading={false}
                disabled
                type='add'
              />
            </div>
          </Tooltip>
        ) : isSpecialOfferIncrementDisabled ? (
          <Tooltip
            id={`add-to-cart-tooltip-${product.code}`}
            title='Osiągnięto maksymalną liczbę produktów z ceną specjalną na jedno zamówienie'
            placement='right'
          >
            <div>
              <IconChangeQuantity
                className='add_to_cart_increment_button_disabled'
                onClick={() => handleIncrementWithState()}
                isLoading={false}
                disabled
                role='button'
                type='add'
                ref={incrementButtonRef}
              />
            </div>
          </Tooltip>
        ) : (
          <IconChangeQuantity
            className='add_to_cart_increment_button'
            type='add'
            role='button'
            onClick={handleIncrementWithState}
            disabled={!!disabledQuantity}
            isLoading={false}
            ref={incrementButtonRef}
          />
        )}
      </div>
    ) : isCollapsible ? (
      <IconChangeQuantity
        className='add_to_cart_increment_button'
        type='add'
        role='button'
        onClick={handleAddWithState}
        disabled={!!disabledQuantity}
        isLoading={false}
        ref={incrementButtonRef}
        standalone={true}
        size={size}
        algoliaInsightsAttributes={algoliaInsightsAttributes}
      />
    ) : (
      <button
        id={product.code}
        className={clsx(
          'add_to_cart_button',
          styles.button,
          variantClass,
          sizeClass
        )}
        onClick={handleAddWithState}
        disabled={false}
        ref={buttonRef}
        {...algoliaInsightsAttributes}
      >
        {icon && <Image src={icon} width={19} height={20} alt='icon' />}
        Do koszyka
      </button>
    )
  }

  const onRemoveQuantityChange = () => {
    handleRemoveFromCartSeo(productQuantity)
    setProductQuantity(0)
  }

  const callback = () => {
    void currentAction?.()
  }

  return (
    <>
      <div className={styles.root}>{displayButtonContent()}</div>
      <RemoveLastProductModal
        cartItemId={cartItemId}
        onQuantityChange={onRemoveQuantityChange}
        product={product}
        open={confirmationModalOpen}
        onClose={handleRemoveModalClose}
      />
      <DeliveryAreaGateModal
        open={postalCodeModalOpen}
        onClose={handleClosePostalCodeModal}
        callback={callback}
      />
    </>
  )
}

export default AddToCartButton
